import React, { useEffect, useState } from "react";
import { CalendarPlus, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { FormField } from "../ui/form-field";
import { DatePickerUS } from "../ui/date-picker-us";
import { TimePicker12h } from "../ui/time-picker-12h";
import { api, formatApiError } from "../../lib/api";
import { NearbyHelpersPanel } from "./NearbyHelpersPanel";
import { useConfirm } from "../../hooks/useConfirm";
import { loadTeamWorkdays } from "../../hooks/useTeamWorkdays";

const JS_DAY_PT = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

const WEEKDAYS = [
  { v: 0, l: "Seg" },
  { v: 1, l: "Ter" },
  { v: 2, l: "Qua" },
  { v: 3, l: "Qui" },
  { v: 4, l: "Sex" },
  { v: 5, l: "Sáb" },
  { v: 6, l: "Dom" },
];

const FREQUENCIES = [
  { value: "Semanal", label: "Semanal" },
  { value: "Quinzenal", label: "Quinzenal" },
  { value: "Mensal", label: "Mensal" },
  { value: "2x por mês", label: "2x por mês" },
];

const FREQ_TWICE_MONTH = "2x por mês";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Modal shown after creating a client. Allows the admin to immediately
 * schedule recurring cleanings — perfect for onboarding clients who
 * already have an agreed schedule.
 *
 *  <ScheduleOnboardingDialog
 *    client={{ id, name, frequency, preferred_days }}
 *    open={open}
 *    onClose={...}
 *  />
 */
export function ScheduleOnboardingDialog({ client, open, onClose }) {
  if (!client) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="schedule-onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="text-blue-600" size={20} />
            Agendar limpezas para {client.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 mb-2">
          Já tem agenda combinada com este cliente? Preencha abaixo para criar
          as limpezas de uma vez. Você pode editar depois na página{" "}
          <strong>Agenda</strong>.
        </p>
        <ScheduleOnboardingForm
          client={client}
          autoLoadTeams={open}
          onScheduled={onClose}
          onSkip={onClose}
          skipLabel="Agendar depois"
          submitLabel="Adicionar à agenda"
          persistConfig
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reusable inner form (without Dialog wrapper) so it can be embedded inside
 * the New Client tabs. Submits to `POST /jobs/bulk` once the client has an `id`.
 *
 * When `client.id` is undefined, the submit button stays disabled and shows
 * a hint telling the admin to save the client first.
 */
export function ScheduleOnboardingForm({
  client,
  autoLoadTeams = true,
  onScheduled,
  onSkip,
  skipLabel,
  submitLabel = "Adicionar à agenda",
  embedded = false,
  onBeforeSchedule,
  onConfigChange,
  persistConfig = false,
  updateLabel = "Atualizar agenda",
}) {
  const [teams, setTeams] = useState([]);
  const confirm = useConfirm();
  const cfg = client?.schedule_config || {};
  const [form, setForm] = useState({
    team_id: cfg.team_id || "",
    start_date: cfg.start_date || todayISO(),
    time: cfg.time || "09:00",
    frequency: cfg.frequency || client?.frequency || "Semanal",
    weekdays: Array.isArray(cfg.weekdays) ? cfg.weekdays : [],
    month_days: Array.isArray(cfg.month_days) ? cfg.month_days : [],
    notes: cfg.notes || "",
  });
  const [saving, setSaving] = useState(false);
  // Captura uma única vez no mount — o onConfigChange depois sobrescreve
  // client.schedule_config com o form (que não carrega saved_at).
  const [initialSavedAt] = useState(() => client?.schedule_config?.saved_at || "");

  function presetWeekdaysFromPt(days) {
    if (!Array.isArray(days)) return [];
    const map = { Seg: 0, Ter: 1, Qua: 2, Qui: 3, Sex: 4, Sáb: 5, Dom: 6 };
    return days.map((d) => map[d]).filter((v) => Number.isFinite(v));
  }

  useEffect(() => {
    if (!autoLoadTeams) return;
    const saved = client?.schedule_config || {};
    setForm((p) => ({
      ...p,
      team_id: saved.team_id || p.team_id || "",
      time: saved.time || p.time || "09:00",
      month_days: Array.isArray(saved.month_days) ? saved.month_days : p.month_days,
      notes: saved.notes ?? p.notes ?? "",
      frequency: saved.frequency || client?.frequency || p.frequency || "Semanal",
      weekdays: Array.isArray(saved.weekdays) && saved.weekdays.length
        ? saved.weekdays
        : presetWeekdaysFromPt(client?.preferred_days || []),
    }));
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadTeams, client?.frequency, client?.preferred_days]);

  // Bubble the current config up so the parent can persist it on the client.
  useEffect(() => {
    if (onConfigChange) onConfigChange(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // Prévia das próximas datas (mesma regra do backend: mesmo dia da semana da 1ª limpeza).
  const [preview, setPreview] = useState([]);
  useEffect(() => {
    const needsWd = form.frequency === "Semanal" || form.frequency === "Quinzenal";
    const needsMd = form.frequency === FREQ_TWICE_MONTH;
    if (!form.start_date
        || (needsWd && form.weekdays.length === 0)
        || (needsMd && !(form.month_days || []).some((d) => d))) {
      setPreview([]);
      return;
    }
    const t = setTimeout(() => {
      api.post("/jobs/bulk/preview", {
        start_date: form.start_date,
        time: form.time,
        frequency: form.frequency,
        weekdays: form.weekdays,
        month_days: (form.month_days || []).filter(Boolean).map(Number),
        limit: 6,
      }).then((r) => setPreview(r.data?.dates || [])).catch(() => setPreview([]));
    }, 350);
    return () => clearTimeout(t);
  }, [form.start_date, form.time, form.frequency, form.weekdays, form.month_days]);

  function upd(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function toggleWeekday(v) {
    setForm((p) => ({
      ...p,
      weekdays: p.weekdays.includes(v)
        ? p.weekdays.filter((x) => x !== v)
        : [...p.weekdays, v].sort(),
    }));
  }

  function setMonthDay(index, raw) {
    const n = parseInt(raw, 10);
    setForm((p) => {
      const md = [...(p.month_days || [])];
      md[index] = Number.isFinite(n) ? Math.min(31, Math.max(1, n)) : "";
      return { ...p, month_days: md };
    });
  }

  async function submit() {
    if (!client?.id) {
      toast.error("Salve o cliente primeiro para poder agendar");
      return;
    }
    if (!client?.contract_end_date) {
      toast.error("Defina a data final do contrato deste cliente antes de agendar");
      return;
    }
    if (!form.team_id) {
      toast.error("Escolha uma equipe");
      return;
    }
    if (
      (form.frequency === "Semanal" || form.frequency === "Quinzenal") &&
      form.weekdays.length === 0
    ) {
      toast.error("Selecione pelo menos um dia da semana");
      return;
    }
    if (form.frequency === FREQ_TWICE_MONTH) {
      const days = (form.month_days || []).filter(
        (d) => Number.isFinite(Number(d)) && Number(d) >= 1 && Number(d) <= 31,
      );
      const unique = [...new Set(days.map(Number))];
      if (unique.length !== 2) {
        toast.error("Escolha 2 dias diferentes do mês (ex: 5 e 20)");
        return;
      }
    }
    // Alerta: dias em que a equipe normalmente não trabalha (getDay 0=Dom).
    const wmap = await loadTeamWorkdays();
    const teamDays = wmap[form.team_id];
    const usesWeekdays = form.frequency === "Semanal" || form.frequency === "Quinzenal";
    const chosen = usesWeekdays
      ? form.weekdays.map((w) => (Number(w) + 1) % 7)
      : form.start_date ? [new Date(`${form.start_date}T12:00:00`).getDay()] : [];
    const off = teamDays ? [...new Set(chosen)].filter((d) => !teamDays.includes(d)) : [];
    if (off.length) {
      const teamName = teams.find((t) => t.id === form.team_id)?.name || "Esta equipe";
      const ok = await confirm({
        title: "Dia fora do normal da equipe",
        description: `${teamName} normalmente não trabalha ${off.map((d) => JS_DAY_PT[d]).join(", ")}. Deseja agendar mesmo assim?`,
        actionLabel: "Agendar mesmo assim",
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const configToPersist = {
        ...form,
        month_days: (form.month_days || [])
          .map(Number)
          .filter((d) => d >= 1 && d <= 31),
        saved_at: new Date().toISOString(),
      };
      if (onBeforeSchedule) {
        await onBeforeSchedule(configToPersist);
      }
      const { data } = await api.post("/jobs/bulk", {
        client_id: client.id,
        team_id: form.team_id,
        start_date: form.start_date,
        time: form.time,
        frequency: form.frequency,
        weekdays: form.weekdays,
        month_days: configToPersist.month_days,
        notes: form.notes,
      });
      // Arquiva a configuração de agendamento no cliente (pré-preenche ao
      // reabrir). No modo embedded isso já é feito via onBeforeSchedule.
      if (persistConfig && client.address) {
        try {
          await api.patch(`/clients/${client.id}`, {
            ...client,
            schedule_config: configToPersist,
          });
        } catch {
          /* não bloqueia o sucesso do agendamento */
        }
      }
      toast.success(`${data.created} limpezas agendadas`);
      if (data.trimmed_by_contract_end > 0) {
        toast.info(
          `${data.trimmed_by_contract_end} foram ignoradas — ultrapassariam o fim do contrato (${data.contract_end_date}).`,
          { duration: 7000 },
        );
      }
      onScheduled && onScheduled(data);
      if (data.skipped_duplicate > 0) {
        toast.info(
          `${data.skipped_duplicate} dia(s) já tinham atendimento deste cliente e foram ignorados (sem duplicar).`,
          { duration: 7000 },
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  const needsWeekdays = form.frequency === "Semanal" || form.frequency === "Quinzenal";
  const needsMonthDays = form.frequency === FREQ_TWICE_MONTH;
  const blocked = !client?.id;
  const missingContractEnd = Boolean(client?.id) && !client?.contract_end_date;

  const savedAt = initialSavedAt;
  let savedAtLabel = "";
  if (savedAt) {
    try {
      savedAtLabel = new Date(savedAt).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      savedAtLabel = "";
    }
  }

  return (
    <div className="space-y-4">
      {savedAtLabel && (
        <div
          data-testid="schedule-saved-banner"
          className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-3 py-2"
        >
          <CalendarPlus size={15} className="flex-shrink-0" />
          <span>
            Rotina já definida — última agenda salva em{" "}
            <strong>{savedAtLabel}</strong>. Os campos abaixo vêm preenchidos com
            a última configuração.
          </span>
        </div>
      )}
      {blocked && (
        <div
          data-testid="schedule-form-blocked"
          className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2"
        >
          Preencha e <strong>salve o cliente</strong> antes de agendar. Você
          pode voltar a esta aba depois de salvar.
        </div>
      )}
      {missingContractEnd && (
        <div
          data-testid="schedule-missing-contract-end"
          className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-3 py-2"
        >
          Defina a <strong>data final do contrato</strong> deste cliente (aba
          Dados gerais) para poder agendar as limpezas até o fim do contrato.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label="Equipe" required>
          <select
            data-testid="onboard-team"
            className="cm-input"
            value={form.team_id}
            onChange={(e) => upd("team_id", e.target.value)}
          >
            <option value="">— selecione —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Frequência" required>
          <select
            data-testid="onboard-frequency"
            className="cm-input"
            value={form.frequency}
            onChange={(e) => upd("frequency", e.target.value)}
          >
            {FREQUENCIES.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Início (MM/DD/YYYY)" required>
          <DatePickerUS
            value={form.start_date}
            onChange={(v) => upd("start_date", v)}
            min={todayISO()}
            testId="onboard-start"
            clearable={false}
          />
        </FormField>
        <FormField label="Horário">
          <TimePicker12h
            testId="onboard-time"
            value={form.time}
            onChange={(v) => upd("time", v)}
          />
        </FormField>
      </div>

      {needsMonthDays && (
        <div className="mt-1" data-testid="onboard-month-days">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
            Quais dias do mês?
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="onboard-month-day-0"
              type="number"
              min="1"
              max="31"
              placeholder="Ex: 5"
              className="cm-input w-24"
              value={form.month_days?.[0] ?? ""}
              onChange={(e) => setMonthDay(0, e.target.value)}
            />
            <span className="text-slate-500 text-sm">e</span>
            <input
              data-testid="onboard-month-day-1"
              type="number"
              min="1"
              max="31"
              placeholder="Ex: 20"
              className="cm-input w-24"
              value={form.month_days?.[1] ?? ""}
              onChange={(e) => setMonthDay(1, e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 mt-1">
            As limpezas serão criadas nesses 2 dias, todo mês, até o fim do contrato.
          </div>
        </div>
      )}

      {needsWeekdays && (
        <div className="mt-1">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
            Dias da semana
          </div>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((w) => {
              const active = form.weekdays.includes(w.v);
              return (
                <button
                  key={w.v}
                  type="button"
                  data-testid={`onboard-wd-${w.v}`}
                  onClick={() => toggleWeekday(w.v)}
                  className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    active
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                  }`}
                >
                  {w.l}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {preview.length > 0 && (
        <div data-testid="onboard-preview" className="mt-1 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">
            Próximas limpezas · {form.frequency}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {preview.map((iso) => {
              const d = new Date(iso);
              const wd = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][d.getDay()];
              const label = `${wd}, ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
              return (
                <span key={iso} className="px-2 py-1 rounded-md bg-white border border-slate-200 text-xs text-slate-700">
                  {label}
                </span>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Sempre no mesmo dia da semana da 1ª limpeza.
          </div>
        </div>
      )}

      <FormField label="Observações (opcional)">
        <textarea
          data-testid="onboard-notes"
          rows={2}
          className="cm-input"
          placeholder="Ex: focar nos banheiros, código da porta…"
          value={form.notes}
          onChange={(e) => upd("notes", e.target.value)}
        />
      </FormField>

      {client?.id && (
        <NearbyHelpersPanel clientId={client.id} teamId={form.team_id} />
      )}

      <div className={`flex gap-2 ${embedded ? "" : "justify-end"}`}>
        {onSkip && skipLabel && (
          <Button
            data-testid="onboard-skip"
            variant="outline"
            onClick={onSkip}
            disabled={saving}
          >
            {skipLabel}
          </Button>
        )}
        <Button
          data-testid="onboard-submit"
          onClick={submit}
          disabled={saving || blocked || missingContractEnd}
        >
          {saving ? (
            <Loader2 size={14} className="mr-2 animate-spin" />
          ) : (
            <Send size={14} className="mr-2" />
          )}
          {saving ? "Agendando…" : (initialSavedAt ? updateLabel : submitLabel)}
        </Button>
      </div>
    </div>
  );
}
